import * as T from "three";
export const RADIUS = 25;
export const SPEED = 6;
const Y = new T.Vector3(0, 1, 0);
export function normalAt([longitude, latitude]) {
  const lon = (longitude * Math.PI) / 180,
    lat = (latitude * Math.PI) / 180;
  return new T.Vector3(
    Math.sin(lon) * Math.cos(lat),
    Math.sin(lat),
    Math.cos(lon) * Math.cos(lat),
  );
}
export function coordinates(point) {
  const n = point.clone().normalize();
  return [
    (Math.atan2(n.x, n.z) * 180) / Math.PI,
    (Math.asin(T.MathUtils.clamp(n.y, -1, 1)) * 180) / Math.PI,
  ];
}
export function surface(point, height = 0) {
  return normalAt(point).multiplyScalar(RADIUS + height);
}
export function distance(a, b) {
  return (
    Math.acos(T.MathUtils.clamp(normalAt(a).dot(normalAt(b)), -1, 1)) * RADIUS
  );
}
export function frameAt(point) {
  const up = normalAt(point),
    lon = (point[0] * Math.PI) / 180;
  const right = new T.Vector3(Math.cos(lon), 0, -Math.sin(lon));
  const north = new T.Vector3().crossVectors(up, right).normalize();
  return {
    up,
    right,
    north,
    rotation: new T.Quaternion().setFromRotationMatrix(
      new T.Matrix4().makeBasis(right, up, north.clone().negate()),
    ),
  };
}
export function place(object, point, height = 0, scale = 1, turn = 0) {
  object.position.copy(surface(point, height));
  object.quaternion
    .copy(frameAt(point).rotation)
    .multiply(new T.Quaternion().setFromAxisAngle(Y, turn));
  object.scale.setScalar(scale);
  return object;
}
// Great-circle stepping and parallel transport avoid longitude seams and polar singularities.
export function stepSurface(position, direction, amount) {
  const up = position.clone().normalize();
  const tangent = direction.clone().addScaledVector(up, -direction.dot(up));
  if (tangent.lengthSq() < 1e-10 || !amount) return position.clone();
  tangent.normalize();
  return up
    .multiplyScalar(Math.cos(amount / RADIUS))
    .addScaledVector(tangent, Math.sin(amount / RADIUS))
    .multiplyScalar(RADIUS);
}

// Tangent offsets use metres, independent of latitude. Local +Z points south.
export function offsetPoint(point, x, z) {
  const { right, north } = frameAt(point);
  return coordinates(surface(point).addScaledVector(right, x).addScaledVector(north, -z));
}
export function pathDistance(point, from, to) {
  const p = normalAt(point), a = normalAt(from), b = normalAt(to);
  const axis = new T.Vector3().crossVectors(a, b);
  if (axis.lengthSq() < 1e-10) return distance(point, from);
  axis.normalize();
  const projected = p.clone().addScaledVector(axis, -p.dot(axis)).normalize();
  if (Math.abs(a.angleTo(projected) + projected.angleTo(b) - a.angleTo(b)) < 1e-6)
    return p.angleTo(projected) * RADIUS;
  return Math.min(distance(point, from), distance(point, to));
}
